import { log } from '../log/log';
import type { MessageEnvelope, PersistenceAdapter } from './syncEngine';

interface Message {
  id: string;
  conversationId: string;
  epoch: number;
  serverSeq: number;
  contentHash: string;
  senderAccountId: string;
  body?: string | undefined;
  deletedAt?: number | undefined;
  sentAt: number;
}

interface Conversation {
  id: string;
  kind: 'direct' | 'group';
  name?: string | undefined;
  archivedAt?: number | undefined;
  lastMessageAt?: number | undefined;
  createdAt: number;
}

interface DbModule {
  messages: { upsert: (message: Message) => Promise<void> };
  conversations: {
    upsert: (conversation: Conversation) => Promise<void>;
    get: (id: string) => Promise<Conversation | undefined>;
  };
}

export class WebPersistenceAdapter implements PersistenceAdapter {
  private getDbModule: () => Promise<DbModule>;

  constructor(getDbModule: () => Promise<DbModule>) {
    this.getDbModule = getDbModule;
  }

  async writeMessage(envelope: MessageEnvelope): Promise<void> {
    const dbModule = await this.getDbModule();
    const message = toMessage(envelope);
    await dbModule.messages.upsert(message);

    log.debug('persistence.web.writeMessage written', {
      contentHash: envelope.contentHash.slice(0, 8),
    });
  }

  async writeBatch(envelopes: MessageEnvelope[]): Promise<void> {
    const dbModule = await this.getDbModule();

    for (const envelope of envelopes) {
      await dbModule.messages.upsert(toMessage(envelope));
    }

    log.debug('persistence.web.writeBatch complete', { count: envelopes.length });
  }

  async updateConversation(conversationId: string, lastMessageAt: number): Promise<void> {
    const dbModule = await this.getDbModule();
    const existing = await dbModule.conversations.get(conversationId);

    const conversation: Conversation = existing
      ? { ...existing, lastMessageAt }
      : {
          id: conversationId,
          kind: 'direct',
          lastMessageAt,
          createdAt: Date.now(),
        };

    await dbModule.conversations.upsert(conversation);
  }
}

function toMessage(envelope: MessageEnvelope): Message {
  return {
    id: envelope.contentHash,
    conversationId: envelope.conversationId,
    epoch: envelope.epoch,
    serverSeq: envelope.serverSeq,
    contentHash: envelope.contentHash,
    senderAccountId: envelope.senderAccountId,
    body: envelope.body,
    sentAt: envelope.sentAt,
  };
}
